import { create } from 'zustand';

import { productInfoService } from '../api';
import type { FetchProductListResponseDTO, UpdateProductPayload } from '../api/dto';
import type { ProductOption, ProductT } from '../types';

type State = {
  groupedProductMap: Map<string, ProductT>;
  productMap: Map<string, ProductT>;
  brandMap: Map<string, ProductBrandOption>;
  lineMap: Map<string, ProductLineOption>;
  productOptionMap: Map<string, ProductOption>;
};

type Actions = {
  fetchProductList: (
    workspaceAlias: string,
    isPublic?: boolean,
  ) => Promise<FetchProductListResponseDTO>;
  setState: StoreSetter<State>;
  initProductStore: () => void;

  addProduct: (product: ProductT) => void;
  addBrand: (brand: ProductBrandOption) => void;
  addLine: (line: ProductLineOption) => void;
};

const initialState: State = {
  groupedProductMap: new Map(),
  productMap: new Map(),
  brandMap: new Map(),
  lineMap: new Map(),
  productOptionMap: new Map(),
};

const useProductStore = create<State & Actions>((set, get) => ({
  ...initialState,
  setState: set,
  initProductStore: () => set(initialState),
  fetchProductList: async (workspaceAlias, isPublic) => {
    const response = await productInfoService.fetchProductList(
      workspaceAlias,
      isPublic,
    );
    set({
      productMap: new Map(
        response.products.map((product) => [product.product_id, product]),
      ),
      groupedProductMap: new Map(
        response.products.map((product) => [product.alias, product]),
      ),
      productOptionMap: new Map(
        response.products
          .flatMap(
            (product) =>
              product.options?.map((option) => ({
                ...option,
                product_alias: product.alias,
              })) || [],
          )
          .map((option) => [option.id, option]),
      ),
      brandMap: new Map(response.brands.map((brand) => [brand.id, brand])),
      lineMap: new Map(response.lines.map((line) => [line.id, line])),
    });
    return response;
  },
  addProduct: (product: ProductT) => {
    set((state) => ({
      productMap: state.productMap.set(product.product_id, product),
    }));
  },
  addBrand: (brand: ProductBrandOption) => {
    set((state) => ({
      brandMap: state.brandMap.set(brand.id, brand),
    }));
  },
  addLine: (line: ProductLineOption) => {
    set((state) => ({
      lineMap: state.lineMap.set(line.id, line),
    }));
  },
  updateProductInfo: (productId: string, payload: UpdateProductPayload) => {
    set((state) => {
      const prevMap = new Map(state.productMap);
      const prevProduct = prevMap.get(productId);
      if (!prevProduct) {
        console.error(`Product ${productId} not found`);
        return state;
      }
      prevMap.set(productId, {
        ...prevProduct,
        product_name: payload.name,
        brand_id: payload.brand_id,
        line_id: payload.line_id,
      });
      return {
        productMap: prevMap,
      };
    });
  },
}));

export default useProductStore;
